import { useTranslation } from 'react-i18next';
import type { Screen } from '@ghostscreen/contracts';
import { formatNumber } from '../../i18n';
import {
  FIXES_PERIODS,
  FIXES_TABS,
  type FixesPeriod,
  type FixesTab,
} from './buildRemoteFixesModel';

const TAB_LABEL_KEY = {
  All: 'tabs.all',
  Waiting: 'tabs.waiting',
  Worked: 'tabs.worked',
  "Didn't work": 'tabs.didntWork',
} as const satisfies Record<FixesTab, string>;

type RemoteFixesToolbarProps = {
  tab: FixesTab;
  tabCounts: Record<FixesTab, number>;
  onTabChange: (tab: FixesTab) => void;
  period: FixesPeriod;
  onPeriodChange: (period: FixesPeriod) => void;
  screenId: string;
  onScreenChange: (screenId: string) => void;
  screens: Screen[];
};

/** Tabs, screen filter, period segment — Ghost Screen Remote Fixes.dc.html. */
export function RemoteFixesToolbar({
  tab,
  tabCounts,
  onTabChange,
  period,
  onPeriodChange,
  screenId,
  onScreenChange,
  screens,
}: RemoteFixesToolbarProps) {
  const { t } = useTranslation('operations');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        {FIXES_TABS.map((item) => {
          const on = item === tab;
          return (
            <button
              key={item}
              type="button"
              onClick={() => onTabChange(item)}
              className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] px-[13px] py-[7px] text-[13.5px] font-bold"
              style={{
                color: on ? 'var(--gs-ink)' : 'var(--gs-ink2)',
                background: 'var(--gs-panel)',
                borderColor: on ? 'var(--gs-accent)' : 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
            >
              {t(TAB_LABEL_KEY[item])}{' '}
              <span className="font-medium" style={{ color: 'var(--gs-muted)' }}>
                {formatNumber(tabCounts[item])}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <select
          value={screenId}
          onChange={(event) => onScreenChange(event.target.value)}
          className="cursor-pointer rounded-[10px] border px-3 py-[9px] text-sm font-semibold outline-none focus:border-[var(--gs-accent)]"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-field)',
            borderColor: 'var(--gs-line)',
            fontFamily: 'inherit',
          }}
        >
          <option value="">{t('allScreens')}</option>
          {screens
            .filter((screen) => screen.deviceId != null)
            .map((screen) => (
              <option key={screen.id} value={screen.id}>
                {screen.name}
              </option>
            ))}
        </select>

        <div
          className="flex gap-1 rounded-[9px] border p-[3px]"
          style={{ background: 'var(--gs-panel2)', borderColor: 'var(--gs-line)' }}
        >
          {FIXES_PERIODS.map((item) => {
            const on = item.id === period;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onPeriodChange(item.id)}
                className="rounded-[7px] border px-[11px] py-1.5 text-[13px] font-bold"
                style={{
                  color: on ? 'var(--gs-ink)' : 'var(--gs-muted)',
                  background: on ? 'var(--gs-panel)' : 'transparent',
                  borderColor: on ? 'var(--gs-line)' : 'transparent',
                  fontFamily: 'inherit',
                }}
              >
                {item.id === '24h'
                  ? t('periods.today')
                  : t('periods.days', { count: item.days })}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
